// api.js - API Service Layer
// Build-time environment variable from vercel.json
const getApiUrl = () => {
  // In production (Vercel), use Vercel's proxy rewrite
  if (process.env.NODE_ENV === "production") {
    // Use relative URL which will be proxied by Vercel to backend
    return "/api";
  }
  // In development, use localhost
  return "http://localhost:3001/api";
};

const API_BASE_URL = getApiUrl();

// Helper function to get auth token
const getAuthToken = () => {
  const user = localStorage.getItem("user");
  if (user) {
    const userData = JSON.parse(user);
    return userData.token;
  }
  return null;
};

// Helper function to create headers
const createHeaders = (includeAuth = true) => {
  const headers = {
    "Content-Type": "application/json",
  };

  if (includeAuth) {
    const token = getAuthToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  return headers;
};

// Generic API request function
const apiRequest = async (endpoint, options = {}) => {
  const { method = "GET", data, includeAuth = true } = options;

  const config = {
    method,
    headers: {
      ...createHeaders(includeAuth),
      // Bypass ngrok warning page
      "ngrok-skip-browser-warning": "true",
    },
  };

  if (data && (method === "POST" || method === "PUT" || method === "PATCH")) {
    config.body = JSON.stringify(data);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    const result = await response.json().catch(() => ({}));

    if (!response.ok) {
      const serverMessage =
        result?.error || result?.message || result?.code || response.statusText;
      const composed = `${serverMessage} (HTTP ${response.status})`;
      // Erro enriquecido (mesmo formato do Monitor 360): permite ao caller ler
      // err.status / err.code / err.data em vez de fazer parse da mensagem.
      const err = new Error(composed || "Erro na requisição");
      err.status = response.status;
      err.code = result?.code || null;
      err.data = result || null;
      throw err;
    }

    return result;
  } catch (error) {
    console.error("API Request Error:", error);
    throw error;
  }
};

// Authentication API
export const authAPI = {
  // Login
  login: async (email, password) => {
    return apiRequest("/auth/login", {
      method: "POST",
      data: { email, password },
      includeAuth: false,
    });
  },

  // Get current user
  getCurrentUser: async () => {
    return apiRequest("/auth/me");
  },

  // Logout
  logout: async () => {
    return apiRequest("/auth/logout", { method: "POST" });
  },

  // Change password
  changePassword: async (currentPassword, newPassword) => {
    return apiRequest("/auth/change-password", {
      method: "POST",
      data: { currentPassword, newPassword },
    });
  },

  // Request password reset
  requestPasswordReset: async (email) => {
    return apiRequest("/auth/request-password-reset", {
      method: "POST",
      data: { email },
      includeAuth: false,
    });
  },

  // Reset password with token
  resetPassword: async (token, newPassword) => {
    return apiRequest("/auth/reset-password", {
      method: "POST",
      data: { token, newPassword },
      includeAuth: false,
    });
  },
};

// Users API
export const usersAPI = {
  // Get all users
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const endpoint = params ? `/users?${params}` : "/users";
    return apiRequest(endpoint);
  },

  // Get user by ID
  getById: async (id) => {
    return apiRequest(`/users/${id}`);
  },

  // Create user
  create: async (userData) => {
    return apiRequest("/users", {
      method: "POST",
      data: userData,
    });
  },

  // Update user
  update: async (id, userData) => {
    return apiRequest(`/users/${id}`, {
      method: "PUT",
      data: userData,
    });
  },

  // Delete user
  delete: async (id) => {
    return apiRequest(`/users/${id}`, {
      method: "DELETE",
    });
  },

  // Toggle user status
  toggleStatus: async (id) => {
    return apiRequest(`/users/${id}/toggle-status`, {
      method: "PATCH",
    });
  },

  // Unlock user (reset login attempts / lockout)
  unlock: async (id) => {
    return apiRequest(`/users/${id}/unlock`, {
      method: "PATCH",
    });
  },

  // Get user statistics
  getStatistics: async () => {
    return apiRequest("/users/statistics");
  },
};

// Companies API
export const companiesAPI = {
  // Get all companies
  getAll: async (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    const endpoint = params ? `/companies?${params}` : "/companies";
    return apiRequest(endpoint);
  },

  // Get company by ID
  getById: async (id) => {
    return apiRequest(`/companies/${id}`);
  },

  // Create company
  create: async (companyData) => {
    return apiRequest("/companies", {
      method: "POST",
      data: companyData,
    });
  },

  // Update company
  update: async (id, companyData) => {
    return apiRequest(`/companies/${id}`, {
      method: "PUT",
      data: companyData,
    });
  },

  // Update company portal password
  updatePortalPassword: async (id, portal_password) => {
    return apiRequest(`/companies/${id}/portal-password`, {
      method: "PATCH",
      data: { portal_password },
    });
  },

  // Upload ICP PFX to Supabase Storage (multipart)
  uploadCertificate: async (companyId, file, pfxPassword) => {
    const formData = new FormData();
    formData.append("certificate", file);
    formData.append("pfx_password", pfxPassword);
    const token = getAuthToken();
    const response = await fetch(
      `${API_BASE_URL}/companies/${companyId}/certificate`,
      {
        method: "POST",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "ngrok-skip-browser-warning": "true",
        },
        body: formData,
      },
    );
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        success: false,
        message: result.message || `Erro HTTP ${response.status}`,
      };
    }
    return {
      success: true,
      data: result.data,
      message: result.message,
    };
  },

  // Remove stored ICP certificate (archive + delete from Storage)
  deleteCertificate: async (companyId) => {
    return apiRequest(`/companies/${companyId}/certificate`, {
      method: "DELETE",
    });
  },

  // Delete company permanently
  delete: async (id) => {
    return apiRequest(`/companies/${id}`, {
      method: "DELETE",
    });
  },

  // Inactivate company (soft delete)
  inactivate: async (id) => {
    return apiRequest(`/companies/${id}/inactivate`, {
      method: "PATCH",
    });
  },

  // Get inactivation logs for a company
  getInactivationLogs: async (id) => {
    return apiRequest(`/companies/${id}/inactivation-logs`);
  },

  // Get latest download job details for a company
  getLastDownloadJob: async (id) => {
    return apiRequest(`/companies/${id}/last-download-job`);
  },

  // Toggle company status
  toggleStatus: async (id) => {
    return apiRequest(`/companies/${id}/toggle-status`, {
      method: "PATCH",
    });
  },

  // Get company users
  getUsers: async (id) => {
    return apiRequest(`/companies/${id}/users`);
  },

  // Get all company-user links for client users
  getAllUserLinks: async () => {
    return apiRequest("/companies/user-links");
  },

  // Link user to company
  linkUser: async (companyId, userId, accessLevel = "read") => {
    return apiRequest(`/companies/${companyId}/users`, {
      method: "POST",
      data: { user_id: userId, access_level: accessLevel },
    });
  },

  // Unlink user from company
  unlinkUser: async (companyId, userId) => {
    return apiRequest(`/companies/${companyId}/users/${userId}`, {
      method: "DELETE",
    });
  },

  // Contatos de e-mail do cliente (avisos de certificado)
  getContacts: async (id) => {
    return apiRequest(`/companies/${id}/contacts`);
  },

  addContact: async (id, contactData) => {
    return apiRequest(`/companies/${id}/contacts`, {
      method: "POST",
      data: contactData,
    });
  },

  updateContact: async (id, contactId, contactData) => {
    return apiRequest(`/companies/${id}/contacts/${contactId}`, {
      method: "PUT",
      data: contactData,
    });
  },

  deleteContact: async (id, contactId) => {
    return apiRequest(`/companies/${id}/contacts/${contactId}`, {
      method: "DELETE",
    });
  },

  // Get company statistics
  getStatistics: async () => {
    return apiRequest("/companies/statistics");
  },

  // Get company statistics for specific period
  getStatisticsForPeriod: async (startDate, endDate) => {
    const params = new URLSearchParams({
      startDate: startDate,
      endDate: endDate,
    });
    return apiRequest(`/companies/statistics/period?${params.toString()}`);
  },

  // Import companies from Excel only
  importFromFile: async (file) => {
    const formData = new FormData();
    formData.append("excelFile", file);

    const token = getAuthToken();
    const config = {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    };

    try {
      const response = await fetch(
        `${API_BASE_URL}/companies/import-excel`,
        config
      );
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Erro na importação");
      }

      return result;
    } catch (error) {
      console.error("CSV Import Error:", error);
      throw error;
    }
  },

  // Export companies to Excel
  exportToExcel: async (filters = {}) => {
    try {
      // Build query string from filters
      const queryParams = new URLSearchParams();
      if (filters.search) queryParams.append("search", filters.search);
      if (filters.status && filters.status !== "all")
        queryParams.append("status", filters.status);
      if (filters.loginStatus && filters.loginStatus !== "all")
        queryParams.append("loginStatus", filters.loginStatus);
      if (filters.certificate === "with" || filters.certificate === "without") {
        queryParams.append("certificate", filters.certificate);
      }
      if (filters.sortBy) queryParams.append("sortBy", filters.sortBy);
      if (filters.sortDirection)
        queryParams.append("sortDirection", filters.sortDirection);

      const queryString = queryParams.toString();
      const apiUrl = getApiUrl();
      const token = getAuthToken();
      const headers = {
        Authorization: `Bearer ${token}`,
        "ngrok-skip-browser-warning": "true",
      };

      const response = await fetch(
        `${apiUrl}/companies/export/excel${queryString ? `?${queryString}` : ""}`,
        {
          method: "GET",
          headers: headers,
        }
      );

      if (!response.ok) {
        // Try to parse error as JSON, but handle binary responses
        let errorData;
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          try {
            errorData = await response.json();
          } catch (e) {
            // If JSON parsing fails, use text
            const errorText = await response.text();
            throw new Error(errorText || "Erro ao exportar empresas para Excel");
          }
        } else {
          const errorText = await response.text();
          throw new Error(errorText || "Erro ao exportar empresas para Excel");
        }
        return {
          success: false,
          message: errorData.message || "Erro ao exportar empresas para Excel",
          error: errorData.error || "Unknown error",
        };
      }

      // Verify content type is Excel
      const contentType = response.headers.get("content-type");
      if (
        contentType &&
        !contentType.includes(
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        )
      ) {
        // If we get HTML (ngrok warning page), try to get error message
        if (contentType.includes("text/html")) {
          const htmlText = await response.text();
          if (htmlText.includes("ngrok") || htmlText.includes("ERR_NGROK")) {
            throw new Error(
              "Erro ao baixar arquivo: página de aviso do ngrok detectada. Verifique a configuração do servidor."
            );
          }
        }
        throw new Error(
          `Tipo de conteúdo inesperado: ${contentType}. Esperado arquivo Excel.`
        );
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = "Empresas.xlsx";

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Convert response to blob
      const blob = await response.blob();

      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      return {
        success: true,
        message: "Arquivo Excel baixado com sucesso",
        filename: filename,
      };
    } catch (error) {
      console.error("Error in exportToExcel:", error);
      return {
        success: false,
        message: "Erro ao exportar empresas para Excel",
        error: error.message,
      };
    }
  },
};

// Dashboard API
export const dashboardAPI = {
  // Get dashboard statistics
  getStats: async () => {
    return apiRequest("/dashboard/stats");
  },

  // Get recent activity
  getActivity: async (limit = 10) => {
    return apiRequest(`/dashboard/activity?limit=${limit}`);
  },

  // Get period statistics
  getPeriodStats: async (startDate, endDate) => {
    const params = new URLSearchParams({
      startDate: startDate,
      endDate: endDate,
    });
    return apiRequest(`/dashboard/period-stats?${params.toString()}`);
  },
};

// Reports API — `grupo`: assescont | comsind | advisa | vazio (todas), igual aos downloads
export const reportsAPI = {
  numeroNotasTodasEmpresas: async (competencia, grupo = "") => {
    const q = new URLSearchParams({
      competencia: competencia,
    });
    if (grupo !== undefined && grupo !== "") q.set("grupo", grupo);
    return apiRequest(`/reports/numero-notas-todas-empresas?${q.toString()}`);
  },
  valorLiquidoTodasEmpresas: async (competencia, empresa = "*", grupo = "") => {
    const q = new URLSearchParams({ competencia });
    if (empresa !== undefined && empresa !== "") q.set("empresa", empresa);
    if (grupo !== undefined && grupo !== "") q.set("grupo", grupo);
    return apiRequest(`/reports/valor-liquido-todas-empresas?${q.toString()}`);
  },
  relacaoRetencoes: async (
    competenciaInicial,
    competenciaFinal,
    empresa = "*",
    tipo = "",
    grupo = "",
    status = "",
  ) => {
    const q = new URLSearchParams({
      competencia_inicial: competenciaInicial,
      competencia_final: competenciaFinal,
    });
    if (empresa !== undefined && empresa !== "") q.set("empresa", empresa);
    if (tipo !== undefined && tipo !== "") q.set("tipo", tipo);
    if (grupo !== undefined && grupo !== "") q.set("grupo", grupo);
    if (status !== undefined && status !== "") q.set("status", status);
    return apiRequest(`/reports/relacao-retencoes?${q.toString()}`);
  },
  relacaoRetencoesPorGrupo: async (
    competenciaInicial,
    competenciaFinal,
    tipo = "",
    empresa = "*",
    grupo = "",
    status = "",
  ) => {
    const q = new URLSearchParams({
      competencia_inicial: competenciaInicial,
      competencia_final: competenciaFinal,
    });
    if (empresa !== undefined && empresa !== "") q.set("empresa", empresa);
    if (tipo !== undefined && tipo !== "") q.set("tipo", tipo);
    if (grupo !== undefined && grupo !== "") q.set("grupo", grupo);
    if (status !== undefined && status !== "") q.set("status", status);
    return apiRequest(`/reports/relacao-retencoes-por-grupo?${q.toString()}`);
  },
  conferenciaHubPortalNotas: async (dateFrom, dateTo, grupo = "") => {
    const q = new URLSearchParams({
      date_from: dateFrom,
      date_to: dateTo,
    });
    if (grupo !== undefined && grupo !== "") q.set("grupo", grupo);
    return apiRequest(`/reports/conferencia-hub-portal-notas?${q.toString()}`);
  },
  // Conferência de Lançamentos (admin): portal x hub x importado, por empresa
  conferenciaLancamentos: async (competencia = "", grupo = "") => {
    const q = new URLSearchParams();
    if (competencia !== undefined && competencia !== "") q.set("competencia", competencia);
    if (grupo !== undefined && grupo !== "") q.set("grupo", grupo);
    return apiRequest(`/reports/conferencia-lancamentos?${q.toString()}`);
  },
  // Integridade da coleta por competência (competencia = MMYYYY)
  integridadeColeta: async (competencia, grupo = "") => {
    const q = new URLSearchParams({ competencia });
    if (grupo !== undefined && grupo !== "") q.set("grupo", grupo);
    return apiRequest(`/reports/integridade-coleta?${q.toString()}`);
  },
  conferenciaChavesHubDominio: async (
    competenciaInicial,
    competenciaFinal,
    empresa = "*",
    tipo = "",
    grupo = "",
  ) => {
    const q = new URLSearchParams({
      competencia_inicial: competenciaInicial,
      competencia_final: competenciaFinal,
    });
    if (empresa !== undefined && empresa !== "") q.set("empresa", empresa);
    if (tipo !== undefined && tipo !== "") q.set("tipo", tipo);
    if (grupo !== undefined && grupo !== "") q.set("grupo", grupo);
    return apiRequest(`/reports/conferencia-chaves-hub-dominio?${q.toString()}`);
  },
  relacaoNotasDivergentes: async (
    dateFrom,
    dateTo,
    empresa = "*",
    tipo = "",
    grupo = "",
    status = "",
  ) => {
    const q = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
    if (empresa !== undefined && empresa !== "") q.set("empresa", empresa);
    if (tipo !== undefined && tipo !== "") q.set("tipo", tipo);
    if (grupo !== undefined && grupo !== "") q.set("grupo", grupo);
    if (status !== undefined && status !== "") q.set("status", status);
    return apiRequest(`/reports/relacao-notas-divergentes?${q.toString()}`);
  },
  notasSemArquivoLocal: async (competencia) => {
    const q = new URLSearchParams({ competencia });
    return apiRequest(`/reports/notas-sem-arquivo-local?${q.toString()}`);
  },
  empresasNaoCadastradas: async (grupo = "") => {
    const q = new URLSearchParams();
    if (grupo !== undefined && grupo !== "") q.set("grupo", grupo);
    return apiRequest(`/reports/empresas-nao-cadastradas?${q.toString()}`);
  },
  empresasProblemasAcesso: async (grupo = "") => {
    const q = new URLSearchParams();
    if (grupo !== undefined && grupo !== "") q.set("grupo", grupo);
    return apiRequest(`/reports/empresas-problemas-acesso?${q.toString()}`);
  },
  // Certificados: vencidos/a vencer + empresas sem certificado (JSON para a tela)
  certificados: async (grupo = "", warnDays = "") => {
    const q = new URLSearchParams();
    if (grupo !== undefined && grupo !== "") q.set("grupo", grupo);
    if (warnDays !== undefined && warnDays !== "") q.set("warn_days", warnDays);
    return apiRequest(`/reports/certificados?${q.toString()}`);
  },
  // Certificados: baixa o .xlsx (mesmo arquivo do e-mail). Segue o padrão de companiesAPI.exportToExcel.
  certificadosExcel: async (grupo = "", warnDays = "") => {
    try {
      const q = new URLSearchParams();
      if (grupo !== undefined && grupo !== "") q.set("grupo", grupo);
      if (warnDays !== undefined && warnDays !== "") q.set("warn_days", warnDays);
      const queryString = q.toString();
      const apiUrl = getApiUrl();
      const token = getAuthToken();

      const response = await fetch(
        `${apiUrl}/reports/certificados/excel${queryString ? `?${queryString}` : ""}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "ngrok-skip-browser-warning": "true",
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Erro ao baixar a planilha de certificados");
      }

      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = "certificados.xlsx";
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) filename = filenameMatch[1];
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      return { success: true, filename };
    } catch (error) {
      console.error("Error in certificadosExcel:", error);
      return {
        success: false,
        message: "Erro ao baixar a planilha de certificados",
        error: error.message,
      };
    }
  },
};

// Downloads API
export const downloadsAPI = {
  // List files/folders from storage via backend
  listFiles: async (path = "") => {
    const query = path ? `?path=${encodeURIComponent(path)}` : "";
    return apiRequest(`/downloads/files${query}`);
  },
  // Get signed download URL for a file
  getSignedUrl: async (path, expiresInSec = 3600, filename) => {
    const params = new URLSearchParams({
      path,
      expiresIn: String(expiresInSec),
      download: "true",
    });
    if (filename) params.set("filename", filename);
    return apiRequest(`/downloads/signed-url?${params.toString()}`);
  },
  // Start period download
  startPeriodDownload: async (startDate, endDate, companyGroup) => {
    return apiRequest("/downloads/start-period", {
      method: "POST",
      data: { startDate, endDate, companyGroup },
    });
  },
  // Start manual download for specific company
  startManualDownload: async (companyId, startDate, endDate) => {
    return apiRequest(`/downloads/start-manual/${companyId}`, {
      method: "POST",
      data: { startDate, endDate },
    });
  },
  // Backfill ADN da frota — status do run corrente (ou do último, vindo do
  // relatório em disco quando o processo reiniciou). Admin e normal.
  getAdnFleetBackfillStatus: async (detalhes = false) => {
    const query = detalhes ? "?detalhes=true" : "";
    return apiRequest(`/downloads/adn/backfill/status${query}`);
  },
  // Dispara o backfill da frota em background (202). Admin only.
  startAdnFleetBackfill: async ({
    mode = "incremental",
    concurrency,
    resume = false,
    companyIds,
  } = {}) => {
    const data = { mode, resume };
    if (concurrency) data.concurrency = concurrency;
    if (companyIds) data.companyIds = companyIds;
    return apiRequest("/downloads/adn/backfill", { method: "POST", data });
  },
  // Esvazia a fila do backfill; as coletas em andamento concluem. Admin only.
  stopAdnFleetBackfill: async () => {
    return apiRequest("/downloads/adn/backfill/stop", { method: "POST" });
  },
  // Get folder information (file count, total size)
  getFolderInfo: async (path = "") => {
    const query = path ? `?path=${encodeURIComponent(path)}` : "";
    return apiRequest(`/downloads/folder-info${query}`);
  },
  // Download folder as ZIP
  downloadFolderZip: async (path = "") => {
    try {
      const query = path ? `?path=${encodeURIComponent(path)}` : "";
      const apiUrl = getApiUrl();
      const headers = createHeaders(true);
      const response = await fetch(`${apiUrl}/downloads/folder-zip${query}`, {
        method: "GET",
        headers: {
          ...headers,
          "ngrok-skip-browser-warning": "true",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Erro ao baixar pasta");
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = "pasta.zip";

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = decodeURIComponent(filenameMatch[1]);
        }
      }

      // Convert response to blob
      const blob = await response.blob();

      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      return {
        success: true,
        message: "Pasta baixada com sucesso",
        filename: filename,
      };
    } catch (error) {
      console.error("Error in downloadFolderZip:", error);
      return {
        success: false,
        message: "Erro ao baixar pasta",
        error: error.message,
      };
    }
  },
  // Download multiple folders as ZIP
  downloadMultipleFolders: async (paths = []) => {
    try {
      const apiUrl = getApiUrl();
      const headers = createHeaders(true);
      const response = await fetch(`${apiUrl}/downloads/folders-zip`, {
        method: "POST",
        headers: {
          ...headers,
          "ngrok-skip-browser-warning": "true",
        },
        body: JSON.stringify({ paths }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Erro ao baixar pastas");
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = "pastas_selecionadas.zip";

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = decodeURIComponent(filenameMatch[1]);
        }
      }

      // Convert response to blob
      const blob = await response.blob();

      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      return {
        success: true,
        message: "Pastas baixadas com sucesso",
        filename: filename,
      };
    } catch (error) {
      console.error("Error in downloadMultipleFolders:", error);
      return {
        success: false,
        message: "Erro ao baixar pastas",
        error: error.message,
      };
    }
  },
};

// Health check API
export const healthAPI = {
  // Check server health
  checkServer: async () => {
    return apiRequest("/health", { includeAuth: false });
  },

  // Check database health
  checkDatabase: async () => {
    return apiRequest("/health/database", { includeAuth: false });
  },
};

// Error handling helper.
// DIVERGÊNCIA INTENCIONAL vs Monitor 360: aqui (Hub/admin) sessão expirada faz
// logout + redirect direto. No Monitor 360, handleAPIError só devolve a mensagem
// e o AuthContext trata a sessão. Não unificar sem alinhar esse comportamento.
export const handleAPIError = (error) => {
  if (
    error.message.includes("Token expirado") ||
    error.message.includes("Token inválido")
  ) {
    // Clear localStorage and redirect to login
    localStorage.removeItem("user");
    window.location.href = "/login";
    return;
  }

  // Return user-friendly error message
  return error.message || "Erro desconhecido";
};
